import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useCreateAddonMutation } from 'src/apiClients';

import { AddonFormFields } from '../addon-form-fields/AddonFormFields';
import type { AddonFormInput, AddonFormValues } from '../addon-form-schema';
import { submitAddonSchema } from '../addon-form-schema';
import { i18n } from './CreateAddonModal.i18n';
import { useStyles } from './CreateAddonModal.styles';

const EMPTY_VALUES: AddonFormInput = {
    name: '',
    type: '',
    quantity: 1,
    salesforceQuoteNumber: '',
    notes: '',
    startDate: '',
    finishDate: '',
};

export interface CreateAddonModalProps {
    subscriptionId: string;
    open: boolean;
    onClose: () => void;
}

/**
 * Creation form for a new subscription (addon) on an instance. The serial
 * number is the server-generated addon id, so it only exists after creation.
 */
export const CreateAddonModal = ({ subscriptionId, open, onClose }: CreateAddonModalProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [createAddon, { isLoading: isCreating }] = useCreateAddonMutation();

    const form = useForm<AddonFormInput, unknown, AddonFormValues>({
        resolver: zodResolver(submitAddonSchema),
        defaultValues: EMPTY_VALUES,
        mode: 'onChange',
    });
    const {
        handleSubmit,
        reset,
        formState: { isValid },
    } = form;

    const handleClose = () => {
        reset(EMPTY_VALUES);
        onClose();
    };

    const onSubmit = handleSubmit(async (values) => {
        try {
            await createAddon({ subscriptionId, ...values }).unwrap();
            enqueueSnackbar(intl.formatMessage(i18n.createSuccess), { variant: 'success' });
            handleClose();
        } catch {
            enqueueSnackbar(intl.formatMessage(i18n.createError), { variant: 'error' });
        }
    });

    return (
        <Dialog
            open={open}
            onClose={isCreating ? undefined : handleClose}
            maxWidth="sm"
            fullWidth
            automation-id="create-addon-modal"
        >
            <DialogTitle automation-id="create-addon-modal-title" className={classes.title}>
                {intl.formatMessage(i18n.title)}
            </DialogTitle>
            <DialogContent>
                <Divider className={classes.divider} />
                <AddonFormFields form={form} />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    disabled={isCreating}
                    automation-id="btn-cancel-addon"
                >
                    {intl.formatMessage(i18n.cancel)}
                </Button>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!isValid || isCreating}
                    automation-id="btn-create-addon"
                >
                    {intl.formatMessage(i18n.create)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
